import type { FC } from 'react';
import React, { useContext, useEffect, useState, useRef } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import {
	useSessionData,
	useBooleanFeatureFlag,
	useMultivariantFeatureFlag,
} from '@confluence/session-data';
import { FlagsStateContainer } from '@confluence/flags';
import {
	useAnnotations,
	useDocumentUpdateStatus,
	useDocumentUpdateStatusDispatch,
} from '@confluence/annotation-provider-store';
import { expValEquals } from '@confluence/feature-experiments';
import { useUnreadCommentsIsEnabled, useUnreadInlineComments } from '@confluence/unread-comments';

import { QuickReloadFlagsI18nProvider } from './QuickReloadFlagsI18nProvider';
import { startQuickReloadPoller } from './QuickReloadPoller';
import { SmartTimer } from './SmartTimer';
import { QuickReloadContext } from './QuickReloadContext';
import { useUserFocusActions } from './useUserFocusState';
import { usePubsubSubscriptions } from './usePubSubs';
import { PUBSUB_FF } from './featureFlags';
import { fetchNotificationsSinceTime } from './pubSubUtils';
import { PageMode } from './pubSubConstants';

type QuickReloadProps = {
	contentId: string;
	// the contentType is used to tailor the flag notification text
	// eg: "Loading new page" vs "Loading new blog post"
	contentType?: string | null;
	lastPollTime: number;
	timerOverride?: any; // expedient; a class with the SmartTimer interface
	pageMode: PageMode;
	pageCommentsSectionInView?: boolean;
	isFabricPage?: boolean;
};

const QuickReloadComponent: FC<QuickReloadProps> = ({
	contentId,
	contentType,
	lastPollTime,
	timerOverride,
	pageMode,
	pageCommentsSectionInView,
	isFabricPage = true,
}) => {
	const { userId } = useSessionData();
	const { documentNeedsUpdate } = useDocumentUpdateStatus();
	const { setDocumentNeedsUpdate } = useDocumentUpdateStatusDispatch();
	const { editorViewProps, viewProps } = useAnnotations();

	const pubSubsEnabled = useBooleanFeatureFlag(PUBSUB_FF);
	const [documentVisible, setDocumentVisible] = useState<boolean>(!document.hidden);
	const flagTimer = useRef<null | NodeJS.Timeout>(null);

	const [lastNotificationReceivedTime, setLastNotificationReceivedTime] = useState(lastPollTime);

	const userFocusActions = useUserFocusActions();

	const isUnreadCommentsIsEnabled = useUnreadCommentsIsEnabled();
	const [, { updateUnreadCommentsOnFirstRender }] = useUnreadInlineComments();

	// Page Comments Auto Update
	let isPageCommentsAutoReloadEnabled = false;
	const isPageCommentsAutoReloadFFEnabled = useBooleanFeatureFlag(
		'confluence.frontend.auto.reload.page.comments',
	);

	if (isPageCommentsAutoReloadFFEnabled) {
		isPageCommentsAutoReloadEnabled = expValEquals<'control' | 'variation'>(
			'auto-reload_page_comments',
			'cohort',
			'variation',
		);
	}

	// Renderer Annotation Provider
	const { cohort: rapCohort } = useMultivariantFeatureFlag(
		'confluence.frontend.renderer.annotation.provider.inline.comments',
		['annotation-provider', 'not-enrolled', 'query-selectors'],
		'not-enrolled',
		true,
	);
	const isRendererAnnotationProviderEnabled = rapCohort === 'annotation-provider';

	useEffect(() => {
		const changeVisibility = () => setDocumentVisible(!document.hidden);
		document.addEventListener('visibilitychange', changeVisibility);
		return () => {
			if (flagTimer.current) {
				clearTimeout(flagTimer.current);
			}
			document.removeEventListener('visibilitychange', changeVisibility);
		};
	}, [setDocumentVisible]);

	const { reloaders } = useContext(QuickReloadContext);

	const {
		pubSubData,
		pubSubState,
		errors: pubSubErrors,
		canUsePubSubData,
	} = usePubsubSubscriptions({
		contentId,
		contentType,
		documentVisible,
		canUsePubSubs: pubSubsEnabled,
		pageMode,
		isPageCommentsAutoReloadEnabled,
		currentAccountId: userId,
		documentNeedsUpdate,
		isFabricPage,
	});

	const userIsFocusedCallback = (flagStateContainer) => {
		userFocusActions.setUserFocusedCallback(flagStateContainer.hideAllFlags);
		return;
	};

	const handleUserReadyCallback = (func, type) => {
		if (flagTimer.current !== null) {
			clearTimeout(flagTimer.current);
		}

		// Delay displaying the flag when the user is ready to disassociate their action
		// with the flag being displayed
		const delayedFunction = () => {
			flagTimer.current = setTimeout(func, 2000);
		};

		userFocusActions.setUserReadyCallback(delayedFunction, type);
	};

	return (
		<Subscribe to={[FlagsStateContainer]}>
			{(flagsStateContainer: FlagsStateContainer) => (
				<QuickReloadFlagsI18nProvider
					flagsStateContainer={flagsStateContainer}
					poller={startQuickReloadPoller}
					contentId={contentId}
					contentType={contentType || 'page'}
					currentAccountId={userId}
					reloadContent={reloaders.content}
					reloadComments={reloaders.comment}
					lastPollTime={lastNotificationReceivedTime}
					setLastNotificationReceivedTime={setLastNotificationReceivedTime}
					timer={timerOverride || SmartTimer}
					documentVisible={documentVisible}
					setUserReadyCallback={handleUserReadyCallback}
					userFocusedCallback={() => userIsFocusedCallback(flagsStateContainer)}
					pubSubData={pubSubData}
					pubSubState={pubSubState}
					canUsePubSub={pubSubsEnabled && canUsePubSubData && !pubSubErrors}
					fetchNotifications={fetchNotificationsSinceTime}
					pageMode={pageMode}
					isPageCommentsAutoReloadEnabled={isPageCommentsAutoReloadEnabled}
					isRendererAnnotationProviderEnabled={isRendererAnnotationProviderEnabled}
					documentNeedsUpdate={Boolean(documentNeedsUpdate)}
					setDocumentNeedsUpdate={setDocumentNeedsUpdate}
					pageCommentsSectionInView={pageCommentsSectionInView}
					isUnreadCommentsIsEnabled={isUnreadCommentsIsEnabled}
					updateUnreadCommentsOnFirstRender={updateUnreadCommentsOnFirstRender}
					selectedAnnotations={
						PageMode.RENDERER === pageMode ? viewProps?.annotations : editorViewProps?.annotations
					}
				/>
			)}
		</Subscribe>
	);
};

export const QuickReload: FC<QuickReloadProps> = (props) => {
	const { userId } = useSessionData();

	// Disable QuickReload for anonymous users completely
	if (!userId) {
		return null;
	}

	return <QuickReloadComponent {...props} />;
};
